import '@fontsource/unbounded/600.css';
import '@fontsource/unbounded/800.css';
import '@fontsource/unbounded/900.css';
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/500.css';
import '@fontsource/rubik/700.css';
import { TextStyle, type TextStyleOptions } from 'pixi.js';

/**
 * Визуальный язык макетов «BioForge — игровое окно»: цвета, шрифты, размер кадра.
 * Экраны свёрстаны в координатах кадра 1440 × 900 и масштабируются под окно.
 */

export const DESIGN_WIDTH = 1440;
export const DESIGN_HEIGHT = 900;

export const COLOR = {
  ink: 0x120b2a,
  night: 0x070a1c,
  deep: 0x0d1b3a,
  sea: 0x1d4a66,
  panelTop: 0x1e3e63,
  panelBottom: 0x0e1c3a,
  border: 0x3a6e92,
  borderDim: 0x23496a,
  text: 0xf2fbff,
  textSoft: 0xb9d6e3,
  textMuted: 0x9fc4d6,
  textFaint: 0x7f9db0,
  mint: 0x7ff5e6,
  mintDark: 0x2fbfae,
  teal: 0x36e2d0,
  gold: 0xffd23a,
  goldSoft: 0xffe08a,
  goldBorder: 0x8a6a2e,
  goldTop: 0x4a3a1a,
  goldBottom: 0x2a1f0c,
  pink: 0xff8fb0,
  pinkDark: 0xd94b8c,
  rose: 0xc2224f,
  white: 0xffffff,
} as const;

export const FONT = {
  display: 'Unbounded, system-ui, sans-serif',
  body: 'Rubik, system-ui, sans-serif',
} as const;

/** Текст макета: заголовки — Unbounded, остальное — Rubik. */
export function textStyle(options: {
  size: number;
  color?: number;
  weight?: TextStyleOptions['fontWeight'];
  display?: boolean;
  /** Тень текста вниз на `shadow` px цвета ink (заголовки макетов). */
  shadow?: number;
  wrap?: number;
  align?: TextStyleOptions['align'];
  letterSpacing?: number;
  lineHeight?: number;
}): TextStyle {
  return new TextStyle({
    fontFamily: options.display ? FONT.display : FONT.body,
    fontSize: options.size,
    fontWeight: options.weight ?? (options.display ? '800' : '400'),
    fill: options.color ?? COLOR.text,
    align: options.align ?? 'left',
    letterSpacing: options.letterSpacing ?? 0,
    lineHeight: options.lineHeight ?? Math.round(options.size * 1.35),
    ...(options.wrap ? { wordWrap: true, wordWrapWidth: options.wrap } : {}),
    ...(options.shadow
      ? {
          dropShadow: {
            color: COLOR.ink,
            alpha: 1,
            angle: Math.PI / 2,
            distance: options.shadow,
            blur: 0,
          },
        }
      : {}),
  });
}

/** Шрифты загружаются до первого кадра: иначе Pixi нарисует текст запасным шрифтом. */
export async function loadFonts(): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  const faces = [
    '600 16px Unbounded',
    '800 16px Unbounded',
    '900 16px Unbounded',
    '400 16px Rubik',
    '500 16px Rubik',
    '700 16px Rubik',
  ];
  try {
    await Promise.all(faces.map((face) => document.fonts.load(face, 'Аа')));
    // Дождаться применения всех начертаний: иначе первый текст измеряется запасным шрифтом
    // и широкие буквы обрезаются (замечено на первом экране телефона).
    await document.fonts.ready;
  } catch {
    // Шрифт не загрузился — игра работает с системным.
  }
}
